import { z } from 'zod'

// Call direction types
export const callDirectionSchema = z.enum(['incoming', 'outgoing'])

// Gender options
export const genderSchema = z.enum(['male', 'female'])

// Prefix options
export const prefixSchema = z.enum([
  'mr',
  'mrs',
  'miss',
  'dr',
  'prof',
  'eng',
  'other',
])

// Call type options
export const callTypeSchema = z.enum([
  'undetermined',
  'general_inquiry',
  'emergency',
  'ambulance_request',
])

// Call category options (dependent on call type)
export const callCategorySchema = z.enum([
  // Undetermined subcategories
  'undetermined/accidental-call',
  'undetermined/dropped-call',
  'undetermined/irrelevant-call',
  // General Inquiry subcategories
  'general-inquiry/service-information',
  'general-inquiry/pricing-request',
  'general-inquiry/follow-up',
  // Emergency subcategories
  'emergency/dispatch-first-responder',
  // Ambulance Request subcategories
  'ambulance-request/dispatch-era-ambulance',
  'ambulance-request/dispatch-3rd-party-ambulance-partner',
  'ambulance-request/interstate-transfer',
  'ambulance-request/interstate-hospital-transfer',
])

// Phone number validation (basic E.164 format)
const phoneNumberSchema = z
  .string()
  .min(1, 'Phone number is required')
  .regex(/^\+?[1-9]\d{1,14}$/, 'Please enter a valid phone number')

// Time format validation (HH:MM:SS)
const timeFormatSchema = z
  .string()
  .regex(
    /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/,
    'Time must be in HH:MM:SS format'
  )

// Date validation (not in the future)
const dateSchema = z
  .union([z.string(), z.date()])
  .transform(val => {
    if (typeof val === 'string') {
      const date = new Date(val)
      if (isNaN(date.getTime())) {
        throw new Error('Invalid date format')
      }
      return date
    }
    return val
  })
  .refine(date => date <= new Date(), 'Date of birth cannot be in the future')

// Main caller information schema
export const callerInformationSchema = z.object({
  // Call Information
  call_direction: callDirectionSchema,

  // Caller Information
  phone_number: phoneNumberSchema,
  call_duration: timeFormatSchema,
  gender: genderSchema,

  // Caller History (read-only, no validation needed)
  caller_history: z.string().optional(),

  // More Caller Information
  prefix: prefixSchema,
  first_name: z
    .string()
    .min(1, 'First name is required')
    .max(50, 'First name must be less than 50 characters')
    .regex(
      /^[a-zA-Z\s'-]+$/,
      'First name can only contain letters, spaces, hyphens, and apostrophes'
    ),
  last_name: z
    .string()
    .min(1, 'Last name is required')
    .max(50, 'Last name must be less than 50 characters')
    .regex(
      /^[a-zA-Z\s'-]+$/,
      'Last name can only contain letters, spaces, hyphens, and apostrophes'
    ),
  date_of_birth: dateSchema,
  location: z
    .string()
    .min(1, 'Location is required')
    .max(200, 'Location must be less than 200 characters'),
  // Optional location coordinates from Google Places
  location_latitude: z.number().optional(),
  location_longitude: z.number().optional(),
  location_state: z.string().optional(),

  // Call Classification
  call_type: callTypeSchema,
  call_category: callCategorySchema,
})

// Export the inferred type
export type CallerInformationFormValues = z.infer<
  typeof callerInformationSchema
>

// Default values for the form
export const defaultCallerInformationValues: Partial<CallerInformationFormValues> =
  {
    call_direction: 'incoming',
    call_duration: '00:00:00',
    caller_history: 'No previous cases found for this caller',
  }

// Validation error messages
export const validationMessages = {
  required: 'This field is required',
  phoneNumber: 'Please enter a valid phone number',
  timeFormat: 'Time must be in HH:MM:SS format',
  futureDate: 'Date of birth cannot be in the future',
  nameFormat: 'Name can only contain letters, spaces, hyphens, and apostrophes',
  maxLength: (max: number) => `Must be less than ${max} characters`,
} as const
